import { useApiMutation } from './useApiMutation';
import { useApiQuery } from './useApiQuery';
import { queryKeys } from './queryKeys';
import { fxRatesService } from '@/services/fxRatesService';
import type {
  FxCurrency,
  FxLatestRate,
  FxRate,
  FxRateBulkCreateRequest,
  FxRateCreateRequest,
  FxRateCreateResponse,
  FxRateSyncRequest,
  FxRateSyncResponse,
} from '@/types/fx-rates';
import { APIError } from '@/utils/api-client';

export const useFxCurrencies = () => {
  return useApiQuery<FxCurrency[]>(
    queryKeys.fxRates.currencies(),
    () => fxRatesService.listCurrencies(),
    {
      enabled: true,
      staleTime: 10 * 60 * 1000,
    }
  );
};

export const useLatestFxRates = (params?: {
  base_currency_id?: string;
  quote_currency_id?: string;
}) => {
  return useApiQuery<FxLatestRate[]>(
    queryKeys.fxRates.latest(params),
    () => fxRatesService.latest(params),
    {
      enabled: true,
      staleTime: 30 * 1000,
    }
  );
};

export const useFxRateHistory = (params?: {
  base_currency_id?: string;
  quote_currency_id?: string;
}) => {
  return useApiQuery<FxRate[]>(
    queryKeys.fxRates.history(params),
    () => fxRatesService.listAll(params),
    {
      enabled: Boolean(params?.base_currency_id && params?.quote_currency_id),
      staleTime: 30 * 1000,
    }
  );
};

export const useCreateFxRate = () => {
  return useApiMutation<FxRateCreateResponse, APIError, FxRateCreateRequest>(
    (payload) => fxRatesService.create(payload),
    {
      invalidateKeys: [queryKeys.fxRates.list(), queryKeys.fxRates.latest()],
      successMessage: 'FX rate created successfully',
    }
  );
};

export const useCreateFxRatesBulk = () => {
  return useApiMutation<
    FxRateCreateResponse[],
    APIError,
    FxRateBulkCreateRequest
  >((payload) => fxRatesService.createBulk(payload), {
    invalidateKeys: [queryKeys.fxRates.list(), queryKeys.fxRates.latest()],
    successMessage: (rows) =>
      `${rows.length} FX rate${rows.length === 1 ? '' : 's'} imported successfully`,
  });
};

export const useSyncFxRatesFromEcb = () => {
  return useApiMutation<FxRateSyncResponse, APIError, FxRateSyncRequest | void>(
    (payload) => fxRatesService.syncFromEcb(payload ?? {}),
    {
      invalidateKeys: [
        queryKeys.fxRates.list(),
        queryKeys.fxRates.latest(),
        queryKeys.fxRates.history(),
      ],
      successMessage: (result) =>
        `ECB sync complete: ${result.rows_inserted} added, ${result.rows_updated} updated`,
    }
  );
};
